import _ from "lodash";

import {
  destinationIsServerSide,
  destinationSupportsTrack,
} from "./destinations/destinations";

export function isCustomEventType(eventType) {
  return (
    eventType === "server" ||
    eventType === "manual_tracked" ||
    eventType === "analytics-android" ||
    eventType === "rn_custom" ||
    eventType === "analytics-ios" ||
    eventType === "sendinblue" ||
    eventType === "mailchimp" ||
    eventType === "intercom"
  );
}

const webEvents = ["pageview", "click", "submit", "change", "manual_tracked"];
const serverEvents = ["server"];
const androidEvents = ["analytics-android"];
const iosEvents = ["analytics-ios"];
const reactNativeEvents = [
  "touch",
  "text_edit",
  "toggle",
  "react_navigation_screenview",
  "rn_custom",
];
const sendInBlueEvents = ["sendinblue"];
const mailchimpEvents = ["mailchimp"];
const intercomEvents = ["intercom"];

// arrayToMap converts the given array to a map where each element of the array
// is a key in the map. The passed in value will be used as the value for all
// keys.
function arrayToMap(arr, val) {
  const result = {};

  for (const elem of arr) {
    result[elem] = val;
  }

  return result;
}

const eventTypeToLibraryMap = _.extend(
  {},
  arrayToMap(webEvents, "web"),
  arrayToMap(reactNativeEvents, "react-native"),
  arrayToMap(androidEvents, "analytics-android"),
  arrayToMap(iosEvents, "analytics-ios"),
  arrayToMap(serverEvents, "server"),
  arrayToMap(sendInBlueEvents, "sendinblue"),
  arrayToMap(mailchimpEvents, "mailchimp"),
  arrayToMap(intercomEvents, "intercom")
);

export const allEventTypes = _.concat(
  webEvents,
  serverEvents,
  androidEvents,
  reactNativeEvents,
  iosEvents,
  sendInBlueEvents,
  mailchimpEvents,
  intercomEvents
);

// libraryForEventType returns the library that sends a specific event type.
export function libraryForEventType(eventType) {
  return eventTypeToLibraryMap[eventType] || "unknown";
}

// eventSourceForEventType returns where the event will come from for the given
// event type.
export function eventSourceForEventType(eventType) {
  if (_.includes(reactNativeEvents, eventType)) {
    return "react-native";
  } else {
    return "autotrack";
  }
}

// Can we send the given event to the given destination? This makes sure that
// the destination has been completely configured and that if we are sending the
// event server side, the destinatino is configured for server side.
export function destinationIsValidForEvent(dest, eventIsServerSide) {
  if (!destinationSupportsTrack(dest.name)) {
    return false;
  }

  if (eventIsServerSide && !destinationIsServerSide(dest.name)) {
    return false;
  }

  if (!eventIsServerSide && !dest.is_valid_client_side) {
    return false;
  }

  if (eventIsServerSide && !dest.is_valid_server_side) {
    return false;
  }

  return true;
}

export function eventDefType(eventDef) {
  // We can determine if an event is a specific kind of autotracked events based
  // on the $event_type filter.
  const eventTypeFilter = eventDef.filter.filters.find(
    (filter) => filter.prop === "$event_type"
  );
  if (eventTypeFilter) {
    return eventTypeFilter.val;
  }

  // We can determine if an event is a server side or custom event based on the mplib filter.
  const lib = eventDef.filter.filters.find((filter) => filter.prop === "mp_lib")
    .val;
  if (
    lib === "server" ||
    lib === "analytics-android" ||
    lib === "analytics-ios"
  ) {
    return lib;
  } else if (lib === "react-native") {
    return "rn_custom";
  } else if (lib === "sendinblue") {
    return "sendinblue";
  } else if (lib === "mailchimp") {
    return "mailchimp";
  } else if (lib === "intercom") {
    return "intercom";
  } else {
    return "manual_tracked";
  }
}

export const eventCategories = [
  { name: "Pageviews", type: "pageview" },
  { name: "Clicks", type: "click" },
  { name: "Submit", type: "submit" },
  { name: "Change", type: "change" },

  { name: "Precision", type: "manual_tracked" },
  { name: "Server", type: "server" },
  { name: "Android", type: "analytics-android" },
  { name: "iOS", type: "analytics-ios" },

  { name: "[React Native] Touch", type: "touch" },
  { name: "[React Native] Edit Text", type: "text_edit" },
  { name: "[React Native] Toggle Switch", type: "toggle" },
  { name: "[React Native] Screen View", type: "react_navigation_screenview" },
  { name: "[React Native] Precision", type: "rn_custom" },

  { name: "Sendinblue", type: "sendinblue" },
  { name: "Mailchimp", type: "mailchimp" },
  { name: "Intercom", type: "intercom" },
];
